import { getCoursePaginated, getCourseJournal } from '@yandex-int/k-common/client/api';
import { LOADING_STATUSES } from '@yandex-int/k-common/client/api/constants';
import { SUBJECTS_SLUGS } from '@yandex-int/k-common/constants';
import userCompare from '@yandex-int/k-common/utils/user-compare.js';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { Spin, showNetworkError, Text } from 'platform-components';
import { getMe } from 'platform-components/src/components/user/selectors';
import { withRouterPath } from 'platform-components/hocs';
import { getTableFromCourseJournal, downloadCSV, downloadFile, tableToCSV } from 'platform-components/utils';
import PropTypes from 'prop-types';
import { stringify, parse } from 'query-string';
import React from 'react';
import { Helmet } from 'react-helmet';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Redirect } from 'react-router';
import ym from 'react-yandex-metrika';
import { compose, bindActionCreators } from 'redux';

import ChangesNotification from 'common.components/changes-notification/changes-notification';
import { getCurrentYearCourseId } from 'common.components/course/selectors';
import { TeacherHeader } from 'common.components/TeacherHeader/TeacherHeader';
import SubjectsNav from 'common.components/subjects-nav/subjects-nav';
import { getCurrentClass } from 'common.components/user/selectors';
import { getFirstActiveTeamId } from 'common.components/user/selectors';
import { actions as coursesActions } from 'store/courses/actions';
import * as coursesSelectors from 'store/courses/selectors';
import { subjectPropType } from 'utils/data-prop-types';
import { getCurrentYearOrSearchStringCourseId } from 'utils/get-course';
import { hasSubjectInLoadedSubjects } from 'utils/has-subject-in-loaded-subjects';

import YearFilter from '../course-year-filter/course-year-filter';
import EmptyState, { EMPTY_STATE_ACTION } from '../empty-state/empty-state';

import { CourseJournalEmptyNoLessons } from './__empty-no-lessons/course-journal__empty-no-lessons';
import { CourseJournalHeader } from './__header/course-journal__header';
import Table from './__table/course-journal__table';
import { getCoursesWithLessons, getJournalCourses } from './selectors';

import './course-journal.scss';

const ORDER_TO_DIRECTION = { asc: -1, desc: 1 };
const DIRECTION_TO_ORDER = { '-1': 'asc', 1: 'desc' };

/**
 * Заменяет объект в поле `lessons` на массив из его значений,
 * при этом упорядочивая их согласно порядку занятий в курсе,
 * Если нет поля `lessons` по какому-либо ученику, то оно
 * создается и заполняется null'ами; заменяет поле `student`
 * объектом юзера этого ученика.
 */
function prepareJournal(journal, course) {
    const students = {};

    course.lessons = course.lessons.filter((lesson) => lesson.date_assignment && !lesson.lesson.structure);
    course.students.forEach((s) => (students[s.id] = s));
    return journal.map((row) => ({
        ...row,
        lessons: course.lessons.map((l) => {
            if (!row.lessons || (l.evaluation_date && !l.is_evaluated)) {
                return { value: null, isCheat: false };
            }

            const value = row.lessons[l.id] || null;
            const isCheat = row.lessons_cheat[l.id]?.pasted;
            return { value, isCheat };
        }),
        efficiencyByProblems: course.lessons.map((lesson) => {
            if (!row.lessons || (lesson.evaluation_date && !lesson.is_evaluated)) {
                return null;
            }

            return row.lessons_stat[lesson.id]?.efficiency_by_problems || null;
        }),
        student: students[row.student],
    }));
}

/**
 * Функция принимает решение по показу таблицы, либо кнопки на скачивание
 * @param {object} journal
 * @param {object} course
 * @returns {{course: any, journal: any, showStudents: boolean}}
 */
function prepareStateData(journal, course) {
    let csvUrl = journal && journal.csv_url ? journal.csv_url : null;
    journal = journal && journal.data ? journal.data.journal : null;

    let showStudents = true;
    let serverDataNotReady = false;

    if (course && journal) {
        journal = prepareJournal(journal, course);
    } else if (!journal) {
        showStudents = false;
        serverDataNotReady = !csvUrl;

        journal = [];
    }

    return {
        course,
        csvUrl,
        journal,
        serverDataNotReady,
        showStudents,
        useUrlToDownloadResults: Boolean(csvUrl),
    };
}

export class CourseJournal extends React.Component {
    static propTypes = {
        allLessonsCounts: PropTypes.object,
        teamId: PropTypes.number.isRequired,
        firstActiveTeamId: PropTypes.number.isRequired,
        courses: PropTypes.array,
        coursesWithLessons: PropTypes.array,
        currentClass: PropTypes.object.isRequired,
        currentYearCourseId: PropTypes.number,
        getRouterPath: PropTypes.func.isRequired,
        history: PropTypes.object.isRequired,
        intl: PropTypes.object.isRequired,
        location: PropTypes.object.isRequired,
        match: PropTypes.object.isRequired,
        me: PropTypes.object.isRequired,
        showNetworkError: PropTypes.func.isRequired,
        subjects: PropTypes.arrayOf(subjectPropType),
        getLessonsCountRequest: PropTypes.func.isRequired,
        getCoursesByTeamRequest: PropTypes.func.isRequired,
        getCoursesByTeamSuccess: PropTypes.func.isRequired,
        shouldLoadCourses: PropTypes.bool.isRequired,
        coursesLoadingStatus: PropTypes.oneOf(Object.values(LOADING_STATUSES)),
    };

    static contextTypes = {
        data: PropTypes.shape({
            getCourses: PropTypes.array,
        }),
        initialRender: PropTypes.bool.isRequired,
        staticUrls: PropTypes.shape({
            forbidden: PropTypes.string.isRequired,
            labCourseJournal: PropTypes.string.isRequired,
        }).isRequired,
        uatraits: PropTypes.object.isRequired,
    };

    constructor(props, context) {
        super(props, context);

        this.state = {
            journalCourses: {},
            loading: false,
            ordering: {},
            currentCourseId: props.currentYearCourseId,
        };

        const { getCoursesByTeamRequest, getCoursesByTeamSuccess, shouldLoadCourses, teamId } = props;
        const { initialRender, data } = context;

        if (initialRender && data.getCourses) {
            getCoursesByTeamSuccess({ teamId, courses: data.getCourses });
        } else if (shouldLoadCourses) {
            getCoursesByTeamRequest({ teamId });
        }
    }

    componentDidMount() {
        const {
            me,
            location: { search },
            courses,
        } = this.props;
        const { courseId } = parse(search);

        if (courseId) {
            this.updateState(courseId);
        } else if (courses.length > 0) {
            const currentCourseId = getCurrentYearOrSearchStringCourseId(me, courses);
            this.updateState(currentCourseId);

            this.loadLessonsCounts();
        }
    }

    // eslint-disable-next-line complexity
    componentDidUpdate(prevProps) {
        const { currentYearCourseId: prevCourseId } = prevProps;
        const {
            history,
            match: {
                params: { subject },
            },
            getRouterPath,
            subjects,
            currentYearCourseId,
            shouldLoadCourses,
            getCoursesByTeamRequest,
            coursesLoadingStatus: loadingStatus,
            teamId,
        } = this.props;
        const {
            staticUrls: { labClassJournal, labSubjectJournal },
        } = this.context;
        const { journalCourses } = this.state;

        if (shouldLoadCourses) {
            getCoursesByTeamRequest({ teamId });
        }

        if (currentYearCourseId && currentYearCourseId !== prevCourseId) {
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({ courseId: currentYearCourseId });
            const { course } = get(journalCourses, currentYearCourseId, {});

            if (!course) {
                this.updateState(currentYearCourseId);
            }
        }

        if (loadingStatus === LOADING_STATUSES.SUCCESS) {
            // если переключаемся на класс, у которого нет предмета из урла, редиректим на первый предмет этого класса
            if (subject && !hasSubjectInLoadedSubjects(subject, subjects)) {
                const url = subjects.length
                    ? getRouterPath(labSubjectJournal, { subject: subjects[0].subject })
                    : getRouterPath(labClassJournal);

                history.replace(`${url}${history.location.search}`);
            }

            if (!subject && subjects?.length > 0) {
                const { subject: availableSubject } = subjects.find((s) => s === SUBJECTS_SLUGS.russian) || subjects[0];

                history.push(
                    `${getRouterPath(labSubjectJournal, { subject: availableSubject })}${history.location.search}`
                );
            }

            this.loadLessonsCounts();
        }
    }

    componentWillUnmount() {
        if (this.loadingPromise) {
            this.loadingPromise.canceled = true;
        }
    }

    redirectToForbiddenUrl() {
        const {
            staticUrls: { forbidden: forbiddenUrl },
        } = this.context;

        const retPath = window.location.pathname;

        window.location.href = `${forbiddenUrl}?${stringify({ retPath })}`;
    }

    updateState = (courseId) => {
        const { journalCourses } = this.state;
        this.setState({ loading: true });

        if (this.loadingPromise) {
            this.loadingPromise.canceled = true;
        }

        const promise = Promise.all([
            getCoursePaginated(courseId, true, true, 'date_assignment_asc'),
            getCourseJournal(courseId),
        ])
            .then(([course, journal]) => {
                if (!promise.canceled) {
                    this.setState({
                        journalCourses: {
                            ...journalCourses,
                            [course.id]: prepareStateData(journal, { ...course }),
                        },
                        courseId: course.id,
                    });
                }
            })
            .catch((error) => {
                if (error.status === 403) {
                    this.redirectToForbiddenUrl();
                } else {
                    showNetworkError();
                }
            })
            .then(() => this.setState({ loading: false }));

        this.loadingPromise = promise;
    };

    onOrderJournalBy = (field) => {
        const { ordering, journalCourses, courseId } = this.state;
        const courseJournal = get(journalCourses, courseId, {});
        const defaultDirection = field === 'student' ? -1 : 1;

        ym('reachGoal', `OrderJournal_by_${field}`);

        // Повторная сортировка по тому же полю меняет порядок
        // сортировки на обратный.
        const direction = field === ordering.field ? -ORDER_TO_DIRECTION[ordering.order] : defaultDirection;

        const newJournal = courseJournal.journal.sort((x, y) => {
            if (field === 'student' || field === 'username') {
                return userCompare(y.student, x.student) * direction;
            }

            /// EDUCATION-1950 В качестве значений может быть число или null(а может и undefined).
            // Чтобы null и числа сравнивались адекватно, а null был меньше нуля - превращаем null в -1
            const defaultValue = -1;
            return (get(y, field, defaultValue) - get(x, field, defaultValue)) * direction;
        });

        this.setState({
            journalCourses: {
                ...journalCourses,
                [courseId]: {
                    ...courseJournal,
                    journal: newJournal,
                },
            },
            ordering: { order: DIRECTION_TO_ORDER[direction], field },
        });
    };

    downloadResult = () => {
        const { uatraits } = this.context;
        const { intl } = this.props;
        const { journalCourses, courseId } = this.state;
        const { course, journal, csvUrl, useUrlToDownloadResults } = journalCourses[courseId];
        const filename = 'students.csv';

        if (useUrlToDownloadResults) {
            downloadFile(csvUrl, filename, uatraits);
        } else {
            const table = getTableFromCourseJournal({
                intl,
                journal,
                lessons: course.lessons.map((entity) => entity.lesson),
                courseId: course.id,
            });

            const csv = tableToCSV(table);

            downloadCSV(csv, filename, true);
        }
    };

    loadLessonsCounts = () => {
        const { allLessonsCounts, courses, getLessonsCountRequest } = this.props;

        courses.forEach((course) => {
            if (isEmpty(allLessonsCounts[course.id])) {
                getLessonsCountRequest({ courseId: course.id });
            }
        });
    };

    handleSelectCourse = (subject) => {
        const {
            staticUrls: { labSubjectJournal },
        } = this.context;
        const { getRouterPath, history } = this.props;

        history.push(`${getRouterPath(labSubjectJournal, { subject })}${history.location.search}`);
    };

    handleSelectYear = ({ target: { value: selectedCourseId } }) => {
        this.updateState(selectedCourseId);
    };

    /* eslint-disable complexity */
    renderContent = () => {
        const {
            teamId,
            coursesWithLessons,
            subjects,
            match: {
                params: { subject },
            },
            coursesLoadingStatus,
        } = this.props;
        const { journalCourses, loading, ordering, courseId } = this.state;

        const { course, journal, serverDataNotReady, showStudents } = get(journalCourses, courseId, {});

        const hasNoLessons = course && course.lessons.length === 0;
        const isCoursesLoaded = coursesLoadingStatus === LOADING_STATUSES.SUCCESS;

        if (isCoursesLoaded) {
            if (subjects.length === 0) {
                return (
                    <EmptyState
                        action={EMPTY_STATE_ACTION.ADD_SUBJECT}
                        locationForMetrika="journal"
                        textId="courseJournal.emptyState.noSubject"
                    />
                );
            }

            return (
                <>
                    {Boolean(course) && (
                        <>
                            <div className="course-journal__courses">
                                <SubjectsNav
                                    currentSubject={subject}
                                    metrikaParams={{ location: 'journal' }}
                                    onSelect={this.handleSelectCourse}
                                    teamId={teamId}
                                />
                                <YearFilter
                                    courseId={course.id}
                                    courses={coursesWithLessons}
                                    mix="course-journal__prev-year-filter"
                                    onSelectCourse={this.handleSelectYear}
                                />
                            </div>
                        </>
                    )}
                    {loading ? (
                        <div className="course-journal__spinner">
                            <Spin center />
                        </div>
                    ) : (
                        <>
                            {course && serverDataNotReady && (
                                <Text id="courseJournal.serverDataNotReady" size={Text.SIZE.XL} />
                            )}
                            {hasNoLessons && <CourseJournalEmptyNoLessons />}
                            {!hasNoLessons && showStudents && course && journal && (
                                <Table
                                    course={course}
                                    journal={journal}
                                    key={course.id}
                                    onOrderJournalBy={this.onOrderJournalBy}
                                    ordering={ordering}
                                />
                            )}
                        </>
                    )}
                </>
            );
        }

        return (
            <div className="course-journal__spinner">
                <Spin center />
            </div>
        );
    };
    /* eslint-enable complexity */

    render() {
        const { currentClass, teamId, firstActiveTeamId, getRouterPath, intl } = this.props;
        const { journalCourses, courseId } = this.state;
        const { staticUrls } = this.context;

        // если teamId – архивный, то редиректим в сохраненный или первый открытый класс
        if (teamId !== firstActiveTeamId) {
            let link = getRouterPath(staticUrls.labJournal);

            if (firstActiveTeamId) {
                link = getRouterPath(staticUrls.labClassJournal, { classId: firstActiveTeamId });
            }

            return <Redirect to={link} />;
        }

        const { serverDataNotReady, useUrlToDownloadResults } = get(journalCourses, courseId, {});

        return (
            <>
                <Helmet title={intl.formatMessage({ id: 'courseJournalPage.title' })} />
                <TeacherHeader className="course-journal__nav" />
                <ChangesNotification />
                <div className="course-journal">
                    <CourseJournalHeader
                        currentClass={currentClass}
                        onDownloadResult={this.downloadResult}
                        showControls={useUrlToDownloadResults && !serverDataNotReady}
                    />
                    {this.renderContent()}
                </div>
            </>
        );
    }
}

const mapStateToProps = (
    storeState,
    {
        match: {
            params: { classId: classIdFromMatch, subject },
        },
    }
) => {
    const classId = Number(classIdFromMatch);

    return {
        allLessonsCounts: coursesSelectors.getAllLessonsCounts(storeState, { classId, subject }),
        teamId: classId,
        firstActiveTeamId: getFirstActiveTeamId(storeState, { classId }),
        courses: getJournalCourses(storeState, { classId, subject }),
        coursesWithLessons: getCoursesWithLessons(storeState, { classId, subject }),
        currentClass: getCurrentClass(storeState, { classId }),
        currentYearCourseId: getCurrentYearCourseId(storeState),
        me: getMe(storeState),
        subjects: coursesSelectors.getSubjects(storeState, { classId }),
        coursesLoadingStatus: coursesSelectors.getCoursesLoadingStatus(storeState, { classId }),
        shouldLoadCourses: coursesSelectors.getShouldLoadCourses(storeState, { classId }),
    };
};

const mapDispatchToProps = (dispatch) => {
    return bindActionCreators(
        {
            showNetworkError,
            getCoursesByTeamSuccess: coursesActions.getCoursesByTeamSuccess,
            getCoursesByTeamRequest: coursesActions.getCoursesByTeamRequest,
            getLessonsCountRequest: coursesActions.getLessonsCountRequest,
        },
        dispatch
    );
};

export default compose(connect(mapStateToProps, mapDispatchToProps), injectIntl, withRouterPath)(CourseJournal);
